/**
 * AffiliateGroupsPage — cohort-labeled grouping for affiliates.
 *
 * Layout:
 *   - Header with brand-green headline.
 *   - "Create group" card: name, hex color (optional), description (optional).
 *   - List table with color swatch, name, description, member_count.
 *   - Row expands to an assign/unassign panel for membership management.
 *
 * Brand: emerald for headline + primary actions. Standard zinc chrome,
 * 6-8px rounding, shadow-sm cards.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAffiliateGroupAssign,
	useAffiliateGroupDetail,
	useAffiliateGroupUnassign,
	useAffiliateGroupsCreate,
	useAffiliateGroupsList,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50";
const BADGE =
	"inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function AffiliateGroupsPage() {
	const listQuery = useAffiliateGroupsList();
	const createMut = useAffiliateGroupsCreate();

	const [name, setName] = useState("");
	const [color, setColor] = useState("");
	const [description, setDescription] = useState("");

	const [expandedId, setExpandedId] = useState<string | null>(null);

	const groups = useMemo(
		() => listQuery.data?.items ?? [],
		[listQuery.data],
	);

	const colorValidOrEmpty = color === "" || HEX_RE.test(color.trim());

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const trimmedName = name.trim();
		if (!trimmedName) return;
		if (!colorValidOrEmpty) return;
		createMut.mutate(
			{
				name: trimmedName,
				color: color.trim() ? color.trim() : null,
				description: description.trim() ? description.trim() : null,
			},
			{
				onSuccess: () => {
					setName("");
					setColor("");
					setDescription("");
				},
			},
		);
	};

	return (
		<main
			data-testid="affiliate-groups-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Network / Affiliates
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Affiliate Groups
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Organize publishers into labeled cohorts. Assign colors and
					descriptions to keep reporting, targeting, and payouts legible
					at a glance.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Create affiliate group"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					Create a group
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Names must be unique per tenant. Colors must be 6-digit hex.
				</p>
				<form
					onSubmit={handleCreate}
					className="mt-4 grid gap-3 sm:grid-cols-[2fr_minmax(7rem,1fr)_3fr_auto]"
					data-testid="affiliate-groups-create-form"
				>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Name
						<input
							type="text"
							className={INPUT}
							placeholder="Tier A publishers"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={createMut.isPending}
							maxLength={80}
							data-testid="affiliate-groups-name-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Color (hex)
						<input
							type="text"
							className={INPUT}
							placeholder="#10b981"
							value={color}
							onChange={(e) => setColor(e.currentTarget.value)}
							disabled={createMut.isPending}
							maxLength={7}
							aria-invalid={!colorValidOrEmpty}
							data-testid="affiliate-groups-color-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Description
						<input
							type="text"
							className={INPUT}
							placeholder="Top-tier partners"
							value={description}
							onChange={(e) => setDescription(e.currentTarget.value)}
							disabled={createMut.isPending}
							maxLength={500}
							data-testid="affiliate-groups-description-input"
						/>
					</label>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending ||
								!name.trim() ||
								!colorValidOrEmpty
							}
							data-testid="affiliate-groups-submit"
						>
							{createMut.isPending ? "Saving." : "Create group"}
						</button>
					</div>
				</form>
				{!colorValidOrEmpty ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="affiliate-groups-color-error"
					>
						Color must be 6-digit hex like #10b981.
					</p>
				) : null}
				{createMut.error ? (
					<p
						className="mt-2 text-xs text-red-600"
						role="alert"
						data-testid="affiliate-groups-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Affiliate groups list">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Groups
					</h2>
					<span className="text-xs text-zinc-500">
						{groups.length} total
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="affiliate-groups-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="affiliate-groups-list-error"
					>
						{listQuery.error.message}
					</p>
				) : groups.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="affiliate-groups-empty"
					>
						No groups yet. Create one above to start organizing publishers.
					</p>
				) : (
					<ul
						className="mt-4 divide-y divide-zinc-200"
						data-testid="affiliate-groups-list"
					>
						{groups.map((group) => {
							const isOpen = expandedId === group.id;
							const colorValue = group.color ?? "#e4e4e7";
							return (
								<li
									key={group.id}
									className="py-3"
									data-testid={`affiliate-groups-row-${group.id}`}
								>
									<div className="flex items-center gap-4">
										<span
											aria-hidden
											className="inline-block h-5 w-5 flex-shrink-0 rounded-full border border-zinc-200"
											style={{ backgroundColor: colorValue }}
											data-testid={`affiliate-groups-swatch-${group.id}`}
										/>
										<div className="flex min-w-0 flex-1 flex-col">
											<div className="flex items-center gap-2">
												<span className="truncate text-sm font-semibold text-zinc-900">
													{group.name}
												</span>
												<span className={BADGE}>
													{group.member_count}{" "}
													{group.member_count === 1
														? "member"
														: "members"}
												</span>
											</div>
											{group.description ? (
												<span className="truncate text-xs text-zinc-500">
													{group.description}
												</span>
											) : null}
										</div>
										<button
											type="button"
											className={BTN_GHOST}
											onClick={() =>
												setExpandedId(isOpen ? null : group.id)
											}
											data-testid={`affiliate-groups-toggle-${group.id}`}
											aria-expanded={isOpen}
										>
											{isOpen ? "Hide members" : "Manage members"}
										</button>
									</div>
									{isOpen ? (
										<MembershipPanel groupId={group.id} />
									) : null}
								</li>
							);
						})}
					</ul>
				)}
			</section>
		</main>
	);
}

interface MembershipPanelProps {
	groupId: string;
}

function MembershipPanel({ groupId }: MembershipPanelProps) {
	const detailQuery = useAffiliateGroupDetail(groupId);
	const assignMut = useAffiliateGroupAssign(groupId);
	const unassignMut = useAffiliateGroupUnassign(groupId);

	const [affiliateId, setAffiliateId] = useState("");

	const handleAssign = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = affiliateId.trim();
		if (!trimmed) return;
		assignMut.mutate(
			{ affiliate_id: trimmed },
			{
				onSuccess: () => setAffiliateId(""),
			},
		);
	};

	const members = detailQuery.data?.members ?? [];

	return (
		<div
			className="mt-3 rounded-[6px] border border-green-100 bg-green-50/40 p-3"
			data-testid={`affiliate-groups-panel-${groupId}`}
		>
			<form
				onSubmit={handleAssign}
				className="flex flex-col gap-2 sm:flex-row"
				data-testid={`affiliate-groups-assign-form-${groupId}`}
			>
				<input
					type="text"
					className={INPUT}
					placeholder="Affiliate ID to assign"
					value={affiliateId}
					onChange={(e) => setAffiliateId(e.currentTarget.value)}
					disabled={assignMut.isPending}
					maxLength={120}
					data-testid={`affiliate-groups-assign-input-${groupId}`}
				/>
				<button
					type="submit"
					className={BTN_PRIMARY}
					disabled={assignMut.isPending || !affiliateId.trim()}
					data-testid={`affiliate-groups-assign-submit-${groupId}`}
				>
					{assignMut.isPending ? "Assigning." : "Assign"}
				</button>
			</form>

			{assignMut.error ? (
				<p className="mt-2 text-xs text-red-600" role="alert">
					{assignMut.error.message}
				</p>
			) : null}
			{unassignMut.error ? (
				<p className="mt-2 text-xs text-red-600" role="alert">
					{unassignMut.error.message}
				</p>
			) : null}

			<div className="mt-3">
				{detailQuery.isLoading ? (
					<p className="text-xs text-zinc-500">Loading members.</p>
				) : detailQuery.error ? (
					<p className="text-xs text-red-600" role="alert">
						{detailQuery.error.message}
					</p>
				) : members.length === 0 ? (
					<p
						className="text-xs text-zinc-500"
						data-testid={`affiliate-groups-members-empty-${groupId}`}
					>
						No members assigned yet.
					</p>
				) : (
					<ul
						className="flex flex-col gap-1"
						data-testid={`affiliate-groups-members-${groupId}`}
					>
						{members.map((memberId) => (
							<li
								key={memberId}
								className="flex items-center justify-between rounded-[4px] bg-white px-2 py-1 text-xs"
							>
								<span className="font-mono text-zinc-700">
									{memberId}
								</span>
								<button
									type="button"
									className={BTN_GHOST}
									onClick={() =>
										unassignMut.mutate({ affiliate_id: memberId })
									}
									disabled={unassignMut.isPending}
									data-testid={`affiliate-groups-unassign-${groupId}-${memberId}`}
								>
									Remove
								</button>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
}

export default AffiliateGroupsPage;
